import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import './RateLimitNotice.css';

export const RATE_LIMIT_MESSAGE = 'The AI service is busy right now (quota reached).';

/**
 * In the chat, instead of the raw provider exception: the AI service ran out of quota for a moment.
 *
 * With `autoRetry`, counts `retryAfterSeconds` down and then calls `onRetry('auto')` — once; the panel decides whether
 * a turn gets an automatic retry at all. "Retry now" calls `onRetry('manual')` at any time, and "Details" unfolds the
 * raw error text for support.
 */
export default function RateLimitNotice({ autoRetry, details, onDismiss, onRetry, retryAfterSeconds }) {
    const [remaining, setRemaining] = useState(retryAfterSeconds);
    const firedRef = useRef(false);
    const onRetryRef = useRef(onRetry);
    onRetryRef.current = onRetry;

    useEffect(() => {
        setRemaining(retryAfterSeconds);
        firedRef.current = false;
    }, [retryAfterSeconds, autoRetry]);

    useEffect(() => {
        if (!autoRetry) { return undefined; }
        if (remaining <= 0) {
            if (!firedRef.current) {
                firedRef.current = true;
                onRetryRef.current('auto');
            }
            return undefined;
        }
        const timer = setTimeout(() => setRemaining((value) => value - 1), 1000);
        return () => clearTimeout(timer);
    }, [autoRetry, remaining]);

    return (
        <div className="rate-limit-notice" role="alert">
            <div className="rate-limit-notice-body">
                <span className="rate-limit-notice-text">
                    {RATE_LIMIT_MESSAGE}
                    {' '}
                    {autoRetry
                        ? <span aria-live="polite">Retrying in {Math.max(remaining, 0)} s…</span>
                        : <span>Try again in a moment.</span>}
                </span>
                <div className="rate-limit-notice-actions">
                    <button className="rate-limit-notice-retry" onClick={() => onRetry('manual')} type="button">Retry now</button>
                    <button aria-label="Dismiss" className="rate-limit-notice-close" onClick={onDismiss} type="button">×</button>
                </div>
            </div>
            {details && (
                <details className="rate-limit-notice-details">
                    <summary>Details</summary>
                    <pre>{details}</pre>
                </details>
            )}
        </div>
    );
}

RateLimitNotice.propTypes = {
    autoRetry: PropTypes.bool,
    details: PropTypes.string,
    onDismiss: PropTypes.func.isRequired,
    onRetry: PropTypes.func.isRequired,
    retryAfterSeconds: PropTypes.number.isRequired,
};
